import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { LOOKBOOK, META } from './data';

// HOME — pattern-paper cream bg with subtle grid overlay. Hero is type-driven
// "house notice" with a single garment portrait. Followed by cloth-swatch ledger.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#E5DDCB] text-[#1F1E1B] relative">
      {/* Subtle pattern-paper grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#1F1E1B 1px, transparent 1px), linear-gradient(90deg, #1F1E1B 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <section className="relative max-w-[1500px] mx-auto px-8 lg:px-14 pt-12 pb-24 grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-20 items-end">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#6B2D2A] mb-6">— A house notice · est. {META.est}</p>
          <h1 className="font-serif text-[44px] sm:text-[72px] lg:text-[120px] leading-[0.92] tracking-[-0.02em]">
            One hundred and four <span className="italic text-[#6B2D2A]">years</span>.<br />Twelve thousand <span className="italic">stitches</span>.<br />One <span className="italic text-[#6B2D2A]">suit</span>.
          </h1>
          <p className="font-serif italic text-xl lg:text-2xl text-[#1F1E1B]/70 mt-10 max-w-md leading-[1.45]">A small bespoke house at no. 14 Savile Row, by appointment.</p>
          <div className="mt-12 flex items-center gap-6">
            <button onClick={() => setPage('lookbook')} className="bg-[#1F1E1B] text-[#E5DDCB] px-9 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-[#6B2D2A] transition-colors" data-testid="tailor-home-lookbook-cta">View the lookbook</button>
            <button onClick={() => setPage('fittings')} className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#6B2D2A] underline-offset-8 hover:text-[#1F1E1B] hover:underline">The six fittings →</button>
          </div>
        </motion.div>
        {/* Featured garment with wax-seal corner */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, delay: 0.2 }} className="relative">
          <div className="relative aspect-[3/4] overflow-hidden border border-[#1F1E1B]/15">
            <img src={LOOKBOOK[0].img} alt="" className="w-full h-full object-cover" />
            {/* Wax-seal ribbon */}
            <div className="absolute -top-2 -right-2 bg-[#6B2D2A] text-[#E5DDCB] w-24 h-24 rounded-full flex items-center justify-center rotate-12 shadow-lg">
              <div className="text-center">
                <p className="font-serif italic text-2xl">A&V</p>
                <p className="font-mono text-[8px] tracking-[0.3em] uppercase">est. 1923</p>
              </div>
            </div>
          </div>
          <div className="bg-[#FAF6E8] border border-[#1F1E1B]/15 border-t-0 p-5">
            <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-[#6B2D2A]" dangerouslySetInnerHTML={{ __html: LOOKBOOK[0].code }} />
            <p className="font-serif text-xl lg:text-2xl mt-1 leading-tight">{LOOKBOOK[0].t}</p>
            <p className="font-serif italic text-sm text-[#1F1E1B]/65 mt-2" dangerouslySetInnerHTML={{ __html: LOOKBOOK[0].cloth }} />
          </div>
        </motion.div>
      </section>

      {/* Cloth ledger - looks like a tailor's swatch book */}
      <section className="relative bg-[#FAF6E8] border-y border-[#1F1E1B]/15">
        <div className="max-w-[1500px] mx-auto px-8 lg:px-14 py-20">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#6B2D2A] mb-3">— Cloth ledger · current bunches</p>
          <h2 className="font-serif text-4xl lg:text-6xl leading-[0.95] mb-12">Sixty-four bunches, <span className="italic text-[#6B2D2A]">in the drawer</span>.</h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-2">
            {[
              ['Holland & Sherry', 'Cape Kid Mohair · 11oz', '1932 house cloth'],
              ['Loro Piana', 'Tasmanian Super 130 · 9oz', 'Drawn 2024'],
              ['Fox Brothers', 'West of England flannel · 14oz', '1957 archive'],
              ['Scabal', 'Black Diamond Super 150 · 8oz', 'Tuxedo standard'],
              ['Caccioppoli', 'Linen-silk-wool · 9oz', 'Summer cloth'],
              ['Dormeuil', 'Cashmere-mohair-silk · 10oz', 'House blend'],
              ['Vitale Barberis', 'Wool-cashmere · 12oz', 'Drawn 2025'],
              ['Dugdale Bros.', 'Pure new wool · 16oz', 'Field cloth'],
            ].map(([maker, cloth, note], i) => (
              <div key={i} className="grid grid-cols-[60px_1fr_auto] gap-4 py-4 border-b border-[#1F1E1B]/10 items-baseline">
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6B2D2A]">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <p className="font-serif text-base lg:text-lg leading-tight">{maker}</p>
                  <p className="font-serif italic text-sm text-[#1F1E1B]/55 mt-0.5">{cloth}</p>
                </div>
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1F1E1B]/45 hidden sm:inline">{note}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setPage('lookbook')} className="mt-10 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B2D2A] hover:text-[#1F1E1B]">
            View the lookbook <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      </section>

      {/* Pull quote - on pattern paper */}
      <section className="relative max-w-[920px] mx-auto px-8 lg:px-14 py-24 text-center">
        <p className="font-serif text-3xl lg:text-5xl leading-[1.2] italic">&ldquo;A bespoke suit takes the time it takes. Six fittings. Six months. One client.&rdquo;</p>
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#6B2D2A] mt-10">— Henry Ashford, Master Cutter, in conversation</p>
      </section>
    </div>
  );
}
